import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { styled } from "@mui/material";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import {
  createTeamBookmark,
  deleteTeamBookmark,
  getTeamBookmarkByTeamId,
  populateTeamBookmarksForUser,
} from "marketplace/bookmarks/_store/bookmarks-redux-slice";
import BookmarkedTeam from "marketplace/bookmarks/team-bookmarks/bookmarked-team";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";

const StyledBookmarkButton = styled(LoadingButton)<{ selected: boolean }>(({ selected, theme }) => ({
  backgroundColor: selected ? theme.palette.primary.main : undefined,
  color: selected ? theme.palette.primary.contrastText : undefined,
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: selected ? theme.palette.primary.main : undefined,
    color: selected ? theme.palette.primary.contrastText : undefined,
  },
}));

export interface TeamBookmarkButtonProps {
  className?: string;
  teamId?: Guid;
}

export default function TeamBookmarkButton(props: Readonly<TeamBookmarkButtonProps>) {
  const { className, teamId } = props;

  const { enqueueSnackbar } = useSnackbar();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();

  const existingBookmark = useSelector(getTeamBookmarkByTeamId(teamId));
  const teamBookmarksLoaded = useSelector((state: RootState) => state.bookmarks.teamBookmarks.entries !== undefined);
  const teamBookmarksLoading = useSelector((state: RootState) => state.bookmarks.teamBookmarks.loading);
  useEffect(() => {
    if (teamBookmarksLoaded || teamBookmarksLoading) return;
    dispatch(populateTeamBookmarksForUser({ session }));
  }, [dispatch, session, teamId, existingBookmark, teamBookmarksLoaded, teamBookmarksLoading]);

  async function createBookmark(teamId: Guid): Promise<BookmarkedTeam | undefined> {
    try {
      const bookmarkedTeam = await dispatch(createTeamBookmark({ teamId, session })).unwrap();
      return BookmarkedTeam.fromJSON(bookmarkedTeam);
    } catch (error) {
      console.error("Failed to create team bookmark:", error);
      return undefined;
    }
  }

  async function deleteBookmark(bookmarkId: Guid): Promise<void> {
    try {
      await dispatch(deleteTeamBookmark({ bookmarkId, session })).unwrap();
    } catch (error) {
      console.error("Failed to delete team bookmark:", error);
    }
  }

  const handleBookmarkClick = async () => {
    try {
      if (existingBookmark) {
        await deleteBookmark(existingBookmark.id);
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!teamId) throw new Error("Bookmark Team: Team ID is undefined");
        await createBookmark(teamId);
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  };

  return (
    <StyledBookmarkButton
      className={className}
      selected={existingBookmark !== undefined}
      variant="outlined"
      aria-label="Bookmark"
      loading={false}
      startIcon={<BookmarkBorderIcon />}
      disableElevation
      disabled={!teamId}
      onClick={handleBookmarkClick}
    >
      {existingBookmark ? "Bookmarked" : "Bookmark"}
    </StyledBookmarkButton>
  );
}
